import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import {
  ChevronRight,
  RefreshCw,
  Edit as EditIcon,
  Copy,
  Trash2,
  FileDown,
  Mail,
  Wrench,
  MoreHorizontal,
  Loader2,
  FileText,
  ExternalLink,
  Building2,
} from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import { ConsumptionChart } from "@/components/ConsumptionChart"
import { StatusDropdown } from "@/components/StatusDropdown"
import { OdooQuoteDialog } from "@/components/OdooQuoteDialog"
import { WinDialog } from "@/components/WinDialog"
import { WonOdooStatus } from "@/components/WonOdooStatus"
import { FollowupCard } from "@/components/FollowupCard"
import { EmailDialog } from "@/components/EmailDialog"
import { KitEditor, type KitLineDraft } from "@/components/KitEditor"
import { getRoleMeta } from "@/components/RoleIcons"
import { confirmDialog } from "@/lib/confirm"
import { fetchSettings, getSimulation, linePriceMultiplier, type SettingsRow } from "@/lib/api"
import {
  formatEur,
  formatKwh,
  formatKwp,
  formatPct,
  formatYears,
  proposalFileBase,
  odooOrderLabel,
} from "@/lib/format"
import { downloadWithFilename, downloadFromBase64 } from "@/lib/download"

interface DetailProps {
  shell: AppShell | null
}

interface SimulationDetail {
  id: string
  number: string
  status: string
  /** BIGINT — pode vir como string (precisão > 2^53). */
  lead_id: number | string | null
  client_name: string
  client_email: string | null
  client_phone: string | null
  address: string | null
  city: string | null
  postal_code: string | null
  latitude: number | string | null
  longitude: number | string | null
  house_type: string | null
  roof_material: string | null
  roof_inclination_type: string | null
  tariff_type: string | null
  phase_type: string | null
  contracted_power_kva: number | string | null
  contracted_power_name: string | null
  has_charger: boolean
  has_meter: boolean
  has_battery: boolean
  has_backup: boolean
  brand_id: number | null
  brand_name: string | null
  brand_solar_slug: string | null
  panel_quantity: number | null
  panel_power_w: number | null
  installed_power_kwp: number | string | null
  applied_margin: number | string | null
  kit_lines_json: unknown
  monthly_production_json: unknown
  monthly_consumption_json: unknown
  financial_results_json: unknown
  total_cost_eur: number | string | null
  total_price_no_vat_eur: number | string | null
  total_price_eur: number | string | null
  annual_savings_eur: number | string | null
  payback_years: number | string | null
  notes: string | null
  created_at: string
  updated_at: string
  has_invoice: boolean
  average_monthly_bill_eur: number | string | null
  price_empty_eur_kwh: number | string | null
  price_peak_eur_kwh: number | string | null
  price_full_eur_kwh: number | string | null
  // Back-link Odoo (preenchidos por push_proposal_to_odoo)
  odoo_order_id: number | string | null
  odoo_order_name: string | null
  odoo_synced_at: string | null
  // Espelho CRM (Frente B — Promote on send; ainda não preenchidos)
  crm_entity_id: number | string | null
  crm_proposal_id: number | string | null
}

interface KitLine {
  type: "product" | "manual"
  product_id?: number | null
  name: string
  description?: string
  qty: number
  unit_price: number | string
  total: number | string
  role?: string
  position?: number
  hidden_in_pdf?: boolean
}

interface MonthlyConsumption {
  month: number
  label: string
  empty: number
  peak: number
  full: number
  total: number
}

interface FinancialResults {
  annual_consumption_kwh: number
  annual_production_kwh: number
  self_consumed_kwh: number
  surplus_kwh: number
  grid_kwh: number
  self_consumption_pct: number
  self_sufficiency_pct: number
  self_consumed_value_eur: number
  surplus_sale_value_eur: number
  annual_savings_eur: number
}

export function Detail({ shell }: DetailProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [sim, setSim] = useState<SimulationDetail | null>(null)
  const [settings, setSettings] = useState<SettingsRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingKit, setEditingKit] = useState(false)
  const [changingStatus, setChangingStatus] = useState(false)
  const [odooOpen, setOdooOpen] = useState(false)
  const [winOpen, setWinOpen] = useState(false)

  const reload = () => {
    if (!shell || !id) return
    getSimulation(shell, id)
      .then((data) => setSim(data as unknown as SimulationDetail))
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Falha a carregar"),
      )
  }

  useEffect(() => {
    if (!shell || !id) return
    setLoading(true)
    Promise.all([getSimulation(shell, id), fetchSettings(shell)])
      .then(([data, s]) => {
        setSim(data as unknown as SimulationDetail)
        setSettings(s)
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Falha a carregar"),
      )
      .finally(() => setLoading(false))
    // reload is intentionally not a dependency — defined inline above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, id])

  async function handleStatusChange(next: string) {
    if (!shell || !sim) return
    // «Ganho» só pelo «Fechar negócio» (relatório, fatura, fotos → Odoo); a
    // encomenda no Odoo é criada aí (antes era automática ao mudar o estado).
    if (next === "ganho") { setWinOpen(true); return }
    setChangingStatus(true)
    try {
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        "actions/solar_change_status",
        { params: { id: sim.id, status: next } },
      )
      if (!res.success) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: "Estado actualizado" })
      // Optimistic — we already know the new value; reload picks up server side
      setSim((cur) => (cur ? { ...cur, status: next } : cur))
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a mudar estado",
      })
    } finally {
      setChangingStatus(false)
    }
  }

  if (loading)
    return <p className="text-sm text-muted-foreground">A carregar simulação…</p>
  if (error) return <p className="text-sm text-destructive">{error}</p>
  if (!sim) return null

  const kitLines = (sim.kit_lines_json as KitLine[]) ?? []
  const monthlyConsumption =
    (sim.monthly_consumption_json as MonthlyConsumption[]) ?? []
  const monthlyProduction = (sim.monthly_production_json as number[]) ?? []
  const financial = sim.financial_results_json as FinancialResults | null

  const kwpStr =
    sim.panel_quantity && sim.panel_power_w
      ? `${sim.panel_quantity} × ${sim.panel_power_w} W = ${formatKwp(
          (Number(sim.panel_quantity) * Number(sim.panel_power_w)) / 1000,
        )}`
      : "—"

  return (
    <div className="space-y-6">
      {/* Breadcrumb + título + ações */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <nav className="flex items-center gap-1 text-xs text-muted-foreground">
            <Link to="/list" className="hover:underline">
              Simulações
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>{sim.number}</span>
          </nav>
          <h1 className="mt-1 flex items-center gap-3 text-2xl font-semibold tracking-tight">
            Simulação {sim.number}
            <StatusDropdown
              value={sim.status}
              onChange={handleStatusChange}
              loading={changingStatus}
              disabled={!shell || (sim.status === "ganho" && !shell.isAdmin)}
            />
            {sim.odoo_order_name ? (
              <span
                className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300"
                title={
                  sim.odoo_synced_at
                    ? `Sincronizada com Odoo em ${new Date(sim.odoo_synced_at).toLocaleString("pt-PT")}`
                    : "Sincronizada com Odoo"
                }
              >
                <Building2 className="h-3 w-3" />
                Odoo: {odooOrderLabel(sim.odoo_order_name, sim.odoo_order_id)}
              </span>
            ) : null}
          </h1>
        </div>
        <ActionBar
          shell={shell}
          sim={sim}
          editingKit={editingKit}
          setEditingKit={setEditingKit}
          onNavigate={navigate}
          onReload={reload}
          onOpenOdoo={() => setOdooOpen(true)}
          locked={sim.status === "ganho"}
        />
      </div>

      <WonOdooStatus shell={shell} proposal={sim as unknown as Record<string, unknown>} onDone={reload} />

      <FollowupCard shell={shell} proposal={sim as unknown as { id: string; odoo_lead_id?: unknown }} />

      {/* PVGIS em baixo: a produção desta simulação foi estimada. */}
      {(financial as { production_estimate?: string | null } | null)?.production_estimate ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          {(financial as { production_estimate?: string }).production_estimate}
          {sim.status !== "ganho" ? " Use «Recalcular» no menu ⋯." : ""}
        </div>
      ) : null}

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard label="Kit Solar" value={kwpStr} />
        <KpiCard label="Marca" value={sim.brand_name ?? "—"} />
        <KpiCard
          label="Média Fatura"
          value={formatEur(sim.average_monthly_bill_eur)}
        />
        <KpiCard
          label="Poupança Anual"
          value={formatEur(sim.annual_savings_eur)}
        />
      </div>

      {/* Cliente + Imóvel */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Cliente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <ClientNameField
              shell={shell}
              name={sim.client_name}
              leadId={sim.lead_id}
            />
            <FieldRow label="Email" value={sim.client_email ?? "—"} />
            <FieldRow label="Telefone" value={sim.client_phone ?? "—"} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Imóvel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <FieldRow label="Tipo" value={sim.house_type ?? "—"} />
            <FieldRow label="Material do telhado" value={sim.roof_material ?? "—"} />
            <FieldRow
              label="Inclinação"
              value={sim.roof_inclination_type ?? "—"}
            />
          </CardContent>
        </Card>
      </div>

      {/* Localização */}
      <Card>
        <CardHeader>
          <CardTitle>Localização</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <FieldRow label="Morada" value={sim.address ?? "—"} />
            <FieldRow label="Cidade" value={sim.city ?? "—"} />
            <FieldRow label="Código postal" value={sim.postal_code ?? "—"} />
          </div>
          {sim.latitude != null && sim.longitude != null && (
            <div className="overflow-hidden rounded-md border border-border">
              <iframe
                title="Mapa satellite"
                className="h-64 w-full"
                src={`https://www.google.com/maps?q=${sim.latitude},${sim.longitude}&z=18&t=k&output=embed`}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detalhes de energia */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Detalhes de Energia</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <FieldRow label="Tarifa" value={sim.tariff_type ?? "—"} />
            <FieldRow label="Fase" value={sim.phase_type ?? "—"} />
            <FieldRow
              label="Potência contratada"
              value={sim.contracted_power_name ?? "—"}
            />
            <FieldRow
              label="Tem fatura"
              value={sim.has_invoice ? "Sim" : "Não"}
            />
            <FieldRow
              label="Preço vazio"
              value={
                sim.price_empty_eur_kwh != null
                  ? formatEur(sim.price_empty_eur_kwh)
                  : "—"
              }
            />
            <FieldRow
              label="Preço cheias"
              value={
                sim.price_full_eur_kwh != null
                  ? formatEur(sim.price_full_eur_kwh)
                  : "—"
              }
            />
            <FieldRow
              label="Preço ponta"
              value={
                sim.price_peak_eur_kwh != null
                  ? formatEur(sim.price_peak_eur_kwh)
                  : "—"
              }
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Opções</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3 text-sm">
            <BoolField label="Carregador" value={sim.has_charger} />
            <BoolField label="Contador" value={sim.has_meter} />
            <BoolField label="Bateria" value={sim.has_battery || sim.has_backup} />
            <BoolField label="Backup" value={sim.has_backup} />
          </CardContent>
        </Card>
      </div>

      {/* Componentes do kit */}
      <Card>
        <CardHeader>
          <CardTitle>
            Componentes do Kit
            {sim.brand_name && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · Marca: {sim.brand_name}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className={editingKit ? "" : "px-0"}>
          {editingKit && settings ? (
            <KitEditor
              shell={shell}
              simulationId={sim.id}
              brandSolarSlug={sim.brand_solar_slug}
              hasBattery={sim.has_battery || sim.has_backup}
              appliedMargin={Number(sim.applied_margin) || settings.default_margin}
              vatMultiplier={settings.vat_multiplier}
              taxExemptionFactor={settings.tax_exemption_factor}
              maxDiscountFraction={settings.max_discount_fraction}
              batteryDiscount={{
                eligible: (sim as unknown as { battery_discount_eligible?: boolean }).battery_discount_eligible === true,
                minQty: Number(settings.battery_discount_min_qty ?? 2) || 2,
                fraction: Number(settings.battery_discount_fraction ?? 0.1) || 0,
              }}
              originalLines={kitLines.map((l, i) => ({
                uid: `${i}-${l.product_id ?? "manual"}`,
                type: l.type,
                product_id: l.product_id ?? null,
                name: l.name,
                description: l.description,
                qty: Number(l.qty),
                unit_price: Number(l.unit_price),
                total: Number(l.total),
                role: l.role,
                position: i,
                hidden_in_pdf: l.hidden_in_pdf ?? false,
              })) as KitLineDraft[]}
              originalDiscountFraction={
                Number(
                  (sim as unknown as { discount_fraction?: number | string }).discount_fraction,
                ) || 0
              }
              originalTotalPriceEur={Number(sim.total_price_eur) || 0}
              originalHidePayback={Boolean(
                (sim as unknown as { hide_payback?: boolean }).hide_payback,
              )}
              onCancel={() => setEditingKit(false)}
              onSaved={() => {
                setEditingKit(false)
                reload()
              }}
            />
          ) : kitLines.length === 0 ? (
            <p className="px-6 text-sm text-muted-foreground">Sem componentes.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead className="text-right">Preço Unit.</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Preço por linha exibido ao cliente, coerente com o motor
                    // (computePrice): linhas COM margem levam margem × (1−ajuste),
                    // as SEM margem (instalação/mão de obra) entram a custo. O
                    // ajuste (desconto/aumento) é distribuído pelas linhas com
                    // margem, por isso o somatório iguala o Total c/ IVA em baixo.
                    const appliedMargin = Number(sim.applied_margin ?? 1)
                    const vat = Number(settings?.vat_multiplier ?? 1.23)
                    const taxEx = Number(settings?.tax_exemption_factor ?? 1)
                    const effVat = 1 + (vat - 1) * taxEx
                    const discountFraction = Number(
                      (sim as unknown as { discount_fraction?: number | string })
                        .discount_fraction ?? 0,
                    )
                    return kitLines.map((l, i) => {
                      const meta = getRoleMeta(l.role)
                      const Icon = meta.Icon
                      const lineMul = linePriceMultiplier({
                        role: l.role,
                        appliedMargin,
                        effectiveVat: effVat,
                        discountFraction,
                        isSolar: true,
                      })
                      const unitFinal = Number(l.unit_price) * lineMul
                      const lineFinal = Number(l.total) * lineMul
                      return (
                        <TableRow key={i}>
                          <TableCell>
                            <Badge variant="outline" className="gap-1">
                              <Icon className={`h-3 w-3 ${meta.color}`} />
                              {meta.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{l.name}</TableCell>
                          <TableCell className="text-right">{l.qty}</TableCell>
                          <TableCell className="text-right">
                            {formatEur(unitFinal)}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatEur(lineFinal)}
                          </TableCell>
                        </TableRow>
                      )
                    })
                  })()}
                </TableBody>
              </Table>
              <Separator className="my-2" />
              <div className="space-y-1 px-6 pb-4 text-sm">
                <PriceRow
                  label="Subtotal sem IVA"
                  value={formatEur(sim.total_price_no_vat_eur)}
                />
                <PriceRow
                  label="Total com IVA"
                  value={formatEur(sim.total_price_eur)}
                  bold
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Gráfico */}
      {monthlyConsumption.length > 0 && monthlyProduction.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Consumo vs Produção Mensal</CardTitle>
          </CardHeader>
          <CardContent>
            <ConsumptionChart
              monthly_consumption={monthlyConsumption}
              monthly_production={monthlyProduction}
            />
          </CardContent>
        </Card>
      )}

      <WinDialog
        open={winOpen}
        onOpenChange={setWinOpen}
        shell={shell}
        kind={"solar"}
        proposalId={sim.id}
        onWon={() => reload()}
        client={{
          name: sim.client_name,
          email: sim.client_email,
          phone: sim.client_phone,
          address: sim.address,
          city: sim.city,
          postal_code: sim.postal_code,
          proposal_number: sim.number,
        }}
      />

      <OdooQuoteDialog
        open={odooOpen}
        onOpenChange={setOdooOpen}
        shell={shell}
        proposalId={sim.id}
        existingOdoo={
          sim.odoo_order_id != null && sim.odoo_order_name
            ? {
                order_id: Number(sim.odoo_order_id),
                order_name: sim.odoo_order_name,
                synced_at: sim.odoo_synced_at,
              }
            : null
        }
        onSynced={() => reload()}
        client={{
          name: sim.client_name,
          email: sim.client_email,
          phone: sim.client_phone,
          address: sim.address,
          city: sim.city,
          postal_code: sim.postal_code,
          proposal_number: sim.number,
        }}
      />

      {/* Resultados financeiros */}
      {financial && (
        <Card>
          <CardHeader>
            <CardTitle>Análise Financeira</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
            <Stat
              label="Consumo anual"
              value={formatKwh(financial.annual_consumption_kwh)}
            />
            <Stat
              label="Produção anual"
              value={formatKwh(financial.annual_production_kwh)}
            />
            <Stat
              label="Autoconsumo"
              value={formatPct(financial.self_consumption_pct / 100)}
            />
            <Stat
              label="Autossuficiência"
              value={formatPct(financial.self_sufficiency_pct / 100)}
            />
            <Stat
              label="Excedente"
              value={formatKwh(financial.surplus_kwh)}
            />
            <Stat
              label="Energia da rede"
              value={formatKwh(financial.grid_kwh)}
            />
            <Stat
              label="Poupança anual"
              value={formatEur(financial.annual_savings_eur)}
            />
            <Stat
              label="Payback"
              value={formatYears(sim.payback_years)}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-2 p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold tracking-tight">{value}</div>
      </CardContent>
    </Card>
  )
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  )
}

function ClientNameField({
  shell,
  name,
  leadId,
}: {
  shell: AppShell | null
  name: string
  leadId: number | string | null
}) {
  const canNavigate = shell != null && leadId != null
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        Nome
      </div>
      {canNavigate ? (
        <button
          type="button"
          onClick={() => {
            // Usa `navigateCrm` via bridge — o parent (CRM host) é quem
            // sabe a origem correcta e navega para o detail do lead.
            // window.open dentro da iframe abre relativo ao origin da
            // iframe (Vite localhost:5173, não o CRM), por isso evitamos.
            const locale =
              (shell?.context as { locale?: string } | undefined)?.locale ??
              "pt"
            shell?.bridge.navigateCrm({
              href: `/${locale}/leads/${leadId}`,
            })
          }}
          className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
          title={`Abrir lead #${leadId} no CRM`}
        >
          {name}
          <ExternalLink className="h-3.5 w-3.5 opacity-70" />
        </button>
      ) : (
        <div className="text-sm font-medium">{name}</div>
      )}
    </div>
  )
}

function BoolField({ label, value }: { label: string; value: boolean }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <Badge variant={value ? "default" : "outline"}>
        {value ? "Sim" : "Não"}
      </Badge>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-base font-semibold">{value}</div>
    </div>
  )
}

function PriceRow({
  label,
  value,
  bold,
  muted,
}: {
  label: string
  value: string
  bold?: boolean
  muted?: boolean
}) {
  return (
    <div
      className={`flex items-center justify-between ${
        muted ? "text-xs text-muted-foreground" : "text-sm"
      } ${bold ? "font-semibold" : ""}`}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}

// ============================================================
// Action buttons (F3)
// ============================================================


// ============================================================
// Action bar: botões primários (Editar Kit, PDF, Email) + dropdown
// (Editar form, Recalcular, Duplicar, PDF Simples, Eliminar).
// ============================================================

function ActionBar({
  shell,
  sim,
  editingKit,
  setEditingKit,
  onNavigate,
  onReload,
  onOpenOdoo,
  locked,
}: {
  shell: AppShell | null
  sim: SimulationDetail
  editingKit: boolean
  setEditingKit: (v: boolean) => void
  onNavigate: (path: string) => void
  onReload: () => void
  onOpenOdoo: () => void
  /** Proposta ganha: sem Editar Kit, editar dados, recalcular nem eliminar. */
  locked: boolean
}) {
  const [busy, setBusy] = useState<string | null>(null)
  const [emailOpen, setEmailOpen] = useState(false)

  async function exportPdf(variant: "full" | "simplified") {
    if (!shell) return
    setBusy(`pdf-${variant}`)
    try {
      const res = await shell.client.post<{
        success: boolean
        data?: {
          public_url: string
          storage_key: string
          pdf_base64?: string | null
          pdf_content_type?: string | null
        }
        error?: string
      }>("actions/solar_export_pdf", { params: { id: sim.id, variant } })
      if (!res.success || !res.data) throw new Error(res.error)
      shell.bridge.toast({
        variant: "success",
        description: `PDF ${variant === "simplified" ? "simplificado" : "completo"} gerado`,
      })
      // Nome do ficheiro = referência da proposta + nome do cliente normalizado.
      // Ex.: SOL-2026-0708-5-Nome-Cliente.pdf
      const filename = `${proposalFileBase(sim.number, sim.client_name)}${
        variant === "simplified" ? "-simples" : ""
      }.pdf`
      // Preferir os bytes base64 (backend buscou-os ao host server-side) →
      // download same-origin com o nome garantido. Fallback: abrir o public_url.
      if (res.data.pdf_base64) {
        downloadFromBase64(
          res.data.pdf_base64,
          filename,
          res.data.pdf_content_type ?? "application/pdf",
        )
        return
      }
      const url = res.data.public_url
      if (!url) return
      let absoluteUrl = url
      if (url.startsWith("/")) {
        const hostOrigin =
          (shell.context as { host_origin?: string }).host_origin ||
          (document.referrer && new URL(document.referrer).origin) ||
          ""
        absoluteUrl = `${hostOrigin}${url}`
      }
      // Nome amigável também no fallback (abrir URL): o r2-proxy usa ?filename=
      // no Content-Disposition, evitando o nome timestamp da key R2.
      absoluteUrl += `${absoluteUrl.includes("?") ? "&" : "?"}filename=${encodeURIComponent(filename)}`
      await downloadWithFilename(absoluteUrl, filename)
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a gerar PDF",
      })
    } finally {
      setBusy(null)
    }
  }


  async function recalc() {
    if (!shell) return
    setBusy("recalc")
    try {
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        "actions/solar_recalc",
        { params: { id: sim.id } },
      )
      if (!res.success) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: "Recalculada" })
      onReload()
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha",
      })
    } finally {
      setBusy(null)
    }
  }

  async function duplicate() {
    if (!shell) return
    setBusy("duplicate")
    try {
      const res = await shell.client.post<{
        success: boolean
        data?: { id: string; number: string }
        error?: string
      }>("actions/solar_duplicate", { params: { id: sim.id } })
      if (!res.success || !res.data) throw new Error(res.error)
      shell.bridge.toast({
        variant: "success",
        description: `Duplicada como ${res.data.number}`,
      })
      onNavigate(`/solar/${res.data.id}`)
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha",
      })
    } finally {
      setBusy(null)
    }
  }

  async function remove() {
    if (!shell) return
    if (!(await confirmDialog(`Eliminar simulação ${sim.number}?`, { destructive: true, confirmLabel: "Eliminar" }))) return
    setBusy("delete")
    try {
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        "actions/solar_delete",
        { params: { id: sim.id } },
      )
      if (!res.success) throw new Error(res.error)
      shell.bridge.toast({ variant: "success", description: "Eliminada" })
      onNavigate("/list")
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha",
      })
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="flex flex-wrap gap-2">
      {!locked && (
        <Button
          variant={editingKit ? "default" : "outline"}
          onClick={() => setEditingKit(!editingKit)}
        >
          <Wrench className="h-4 w-4" />
          {editingKit ? "Sair edição" : "Editar Kit"}
        </Button>
      )}

      <Button
        variant="outline"
        onClick={() => exportPdf("full")}
        disabled={busy === "pdf-full"}
      >
        {busy === "pdf-full" ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <FileDown className="h-4 w-4" />
        )}
        PDF
      </Button>

      <Button variant="outline" onClick={() => setEmailOpen(true)}>
        <Mail className="h-4 w-4" />
        Email
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Mais opções">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[12rem]">
          {!locked && (
            <>
              <DropdownMenuItem onSelect={() => onNavigate(`/solar/${sim.id}/editar`)}>
                <EditIcon className="h-4 w-4" />
                Editar dados
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => recalc()} disabled={busy === "recalc"}>
                <RefreshCw
                  className={`h-4 w-4 ${busy === "recalc" ? "animate-spin" : ""}`}
                />
                Recalcular
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuItem
            onSelect={() => duplicate()}
            disabled={busy === "duplicate"}
          >
            <Copy className="h-4 w-4" />
            Duplicar
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onSelect={() => exportPdf("simplified")}
            disabled={busy === "pdf-simplified"}
          >
            <FileText className="h-4 w-4" />
            PDF Simplificado
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onOpenOdoo()}>
            <Building2 className="h-4 w-4" />
            {sim.odoo_order_id ? "Re-enviar para Odoo" : "Enviar para Odoo"}
          </DropdownMenuItem>
          {!locked && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem destructive onSelect={() => remove()}>
                <Trash2 className="h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <EmailDialog
        shell={shell}
        open={emailOpen}
        onOpenChange={setEmailOpen}
        proposalId={sim.id}
        number={sim.number}
        clientName={sim.client_name}
        clientEmail={sim.client_email}
        exportVerb="solar_export_pdf"
        sendVerb="solar_send_email"
      />
    </div>
  )
}

